import JSZip from "jszip";

export type ProjectFile = { name: string; content: string };

/**
 * Add build scaffolding so the exported project runs standalone with `npm install && npm run dev`.
 * Mirrors the edge function's ensureBuildScaffolding but adds Tailwind + PostCSS for CSS support.
 */
function ensureBuildScaffolding(files: ProjectFile[], projectName: string): ProjectFile[] {
  const out = [...files];
  const has = (name: string) => out.some((f) => f.name === name);

  if (!has("package.json")) {
    out.push({
      name: "package.json",
      content: JSON.stringify(
        {
          name: projectName,
          private: true,
          type: "module",
          scripts: {
            dev: "vite",
            build: "vite build",
            preview: "vite preview",
          },
          dependencies: {
            react: "^18.3.1",
            "react-dom": "^18.3.1",
            "react-router-dom": "^6.28.0",
            "@supabase/supabase-js": "^2.47.0",
          },
          devDependencies: {
            "@types/react": "^18.3.3",
            "@types/react-dom": "^18.3.0",
            "@types/node": "^22.0.0",
            "@vitejs/plugin-react": "^4.3.1",
            autoprefixer: "^10.4.21",
            postcss: "^8.5.6",
            tailwindcss: "^3.4.17",
            typescript: "^5.5.3",
            vite: "^5.4.1",
          },
        },
        null,
        2
      ),
    });
  }

  if (!has("vite.config.ts") && !has("vite.config.js")) {
    out.push({
      name: "vite.config.ts",
      content: [
        "import { defineConfig } from 'vite';",
        "import react from '@vitejs/plugin-react';",
        "import path from 'path';",
        "",
        "export default defineConfig({",
        "  plugins: [react()],",
        "  resolve: {",
        "    alias: {",
        "      '@': path.resolve(__dirname, './src'),",
        "    },",
        "  },",
        "});",
        "",
      ].join("\n"),
    });
  }

  if (!has("tsconfig.json")) {
    out.push({
      name: "tsconfig.json",
      content: JSON.stringify(
        {
          compilerOptions: {
            target: "ES2020",
            useDefineForClassFields: true,
            lib: ["ES2020", "DOM", "DOM.Iterable"],
            module: "ESNext",
            skipLibCheck: true,
            moduleResolution: "bundler",
            allowImportingTsExtensions: true,
            isolatedModules: true,
            moduleDetection: "force",
            noEmit: true,
            jsx: "react-jsx",
            strict: false,
            baseUrl: ".",
            paths: {
              "@/*": ["./src/*"],
            },
          },
          include: ["src"],
        },
        null,
        2
      ),
    });
  }

  if (!has("tailwind.config.js") && !has("tailwind.config.ts")) {
    out.push({
      name: "tailwind.config.js",
      content: [
        "/** @type {import('tailwindcss').Config} */",
        "export default {",
        '  content: ["./index.html", "./src/**/*.{js,ts,jsx,tsx}"],',
        "  theme: { extend: {} },",
        "  plugins: [],",
        "};",
        "",
      ].join("\n"),
    });
  }

  if (!has("postcss.config.js") && !has("postcss.config.cjs")) {
    out.push({
      name: "postcss.config.js",
      content: [
        "export default {",
        "  plugins: {",
        "    tailwindcss: {},",
        "    autoprefixer: {},",
        "  },",
        "};",
        "",
      ].join("\n"),
    });
  }

  return out;
}

/** Strip the Tailwind CDN <script> tag from index.html since the export uses real Tailwind. */
function stripTailwindCdn(files: ProjectFile[]): ProjectFile[] {
  return files.map((f) => {
    if (f.name !== "index.html") return f;
    return {
      ...f,
      content: f.content.replace(
        /\s*<script[^>]*cdn\.tailwindcss\.com[^>]*><\/script>\s*/g,
        "\n"
      ),
    };
  });
}

function makeReadme(projectName: string): string {
  return [
    `# ${projectName}`,
    "",
    "Built with [DayOne](https://usedayone.net).",
    "",
    "## Getting started",
    "",
    "1. Install dependencies:",
    "",
    "```bash",
    "npm install",
    "```",
    "",
    "2. (Optional) Configure Supabase for auth/database:",
    "",
    "```bash",
    "cp .env.example .env",
    "# Edit .env with your Supabase URL and anon key",
    "```",
    "",
    "3. Start the dev server:",
    "",
    "```bash",
    "npm run dev",
    "```",
    "",
    "## Build for production",
    "",
    "```bash",
    "npm run build",
    "```",
    "",
    "Output goes to `dist/`.",
    "",
    "## Environment Variables",
    "",
    "| Variable | Description |",
    "|----------|-------------|",
    "| `VITE_SUPABASE_URL` | Your Supabase project URL |",
    "| `VITE_SUPABASE_ANON_KEY` | Your Supabase anon/public key |",
    "",
    "Get these from [supabase.com/dashboard](https://supabase.com/dashboard) → Settings → API.",
    "",
    "Without these, auth will run in preview mode (mock data, no persistence).",
    "",
  ].join("\n");
}

/** Add .env.example for all projects (Supabase auth support). */
function addEnvExample(files: ProjectFile[]): ProjectFile[] {
  const has = (name: string) => files.some((f) => f.name === name);
  if (has(".env.example") || has(".env")) return files;

  return [
    ...files,
    {
      name: ".env.example",
      content: [
        "# DayOne App — Supabase credentials",
        "# Get these from your Supabase project: https://supabase.com/dashboard → Settings → API",
        "",
        "VITE_SUPABASE_URL=https://your-project-id.supabase.co",
        "VITE_SUPABASE_ANON_KEY=your-anon-key-here",
        "",
      ].join("\n"),
    },
  ];
}

/** Check if the project has database files (migrations/seed) and add setup docs. */
function addDayOneDBExportFiles(files: ProjectFile[], projectName: string): ProjectFile[] {
  const out = [...files];
  const has = (name: string) => out.some((f) => f.name === name);
  const hasMigrations = out.some((f) => f.name.startsWith("supabase/migrations/"));

  if (!hasMigrations) return out;

  // Add SETUP.md with database setup instructions
  if (!has("SETUP.md")) {
    out.push({
      name: "SETUP.md",
      content: [
        `# ${projectName} — Database Setup`,
        "",
        "This project includes a DayOne DB schema. Follow these steps to connect your own Supabase project.",
        "",
        "## 1. Create a Supabase project",
        "",
        "1. Go to [supabase.com/dashboard](https://supabase.com/dashboard)",
        "2. Click **New Project**",
        "3. Choose a name, password, and region",
        "4. Wait for the project to be ready (~30 seconds)",
        "",
        "## 2. Apply the database schema",
        "",
        "Option A — Using the Supabase SQL editor:",
        "1. Go to your project's **SQL Editor**",
        "2. Paste the contents of `supabase/migrations/001_initial.sql` and run it",
        "3. (Optional) Paste `supabase/seed.sql` to add sample data",
        "",
        "Option B — Using the Supabase CLI:",
        "```bash",
        "npx supabase init  # if not already initialized",
        "npx supabase db push",
        "```",
        "",
        "## 3. Configure environment variables",
        "",
        "1. Copy `.env.example` to `.env`",
        "2. Fill in your Supabase project URL and anon key from the project dashboard (Settings > API)",
        "",
        "```bash",
        "cp .env.example .env",
        "```",
        "",
        "## 4. Run the app",
        "",
        "```bash",
        "npm install",
        "npm run dev",
        "```",
        "",
        "The app will connect to your Supabase database automatically.",
        "",
        "---",
        "",
        "Built with [DayOne](https://usedayone.net)",
        "",
      ].join("\n"),
    });
  }

  return out;
}

/**
 * Bundle project files into a ZIP with build scaffolding and README, then trigger browser download.
 */
export async function downloadProjectAsZip(
  files: ProjectFile[],
  projectName: string
): Promise<void> {
  const filtered = files.filter((f) => !f.name.includes("node_modules"));
  if (filtered.length === 0) throw new Error("No files to export");

  const safeName = projectName.replace(/[^a-zA-Z0-9-_]/g, "-").slice(0, 50) || "project";

  let exportFiles = stripTailwindCdn(filtered);
  exportFiles = ensureBuildScaffolding(exportFiles, safeName);
  exportFiles = addEnvExample(exportFiles);
  exportFiles = addDayOneDBExportFiles(exportFiles, projectName);
  exportFiles.push({ name: "README.md", content: makeReadme(projectName) });

  const zip = new JSZip();
  for (const { name, content } of exportFiles) {
    zip.file(name, content);
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeName}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}
